import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useMsal } from "@azure/msal-react";
import { b2cConfig, loginScopes, policies } from "../config/msalConfig";
import { signOut, setTokens, IdTokenClaims } from "../store/slices/authSlice";
import { logger } from "../services/logger.service";

/**
 * Custom hook for handling Azure AD B2C policy-based authentication flows
 *
 * This hook provides:
 * - Automatic redirect promise handling (Phase 7 requirement)
 * - Password reset flow detection and redirect
 * - Policy validation (ensures signin policy is used)
 * - B2C error handling (AADB2C90118, AADB2C90091)
 * - Token storage in Redux
 * - Manual policy methods (resetPassword)
 *
 * Performance Optimizations:
 * - redirectHandled ref prevents duplicate handleRedirectPromise calls
 *
 * Error Codes:
 * - AADB2C90118: User forgot password (redirect to reset password policy)
 * - AADB2C90091: User cancelled flow (logout and redirect to signin)
 */
export function useB2CPolicies() {
  const { instance } = useMsal();
  const dispatch = useDispatch();
  const redirectHandled = useRef(false);

  /**
   * Phase 7: Automatic redirect promise handling
   * Handles B2C redirect callbacks and policy-specific flows
   * Uses redirectHandled ref to prevent duplicate processing
   */
  useEffect(() => {
    if (redirectHandled.current) {
      return;
    }
    redirectHandled.current = true;

    const handleRedirectPromise = async () => {
      try {
        const response = await instance.handleRedirectPromise();

        if (response) {
          // Store tokens in Redux
          dispatch(
            setTokens({
              accessToken: response.accessToken,
              idTokenClaims: response.idTokenClaims as IdTokenClaims,
            }),
          );

          // Check for password reset flow
          // When user completes password reset, tfp claim will be "resetpassword"
          const tfp = (response.account?.idTokenClaims as any)?.tfp;
          if (tfp?.toLowerCase() === policies.resetpassword.toLowerCase()) {
            // Password was reset, redirect to sign in
            instance.loginRedirect({
              authority: import.meta.env.VITE_B2C_CONFIG_INSTANCE,
              scopes: loginScopes,
            });
            return;
          }

          // Check if account matches signin policy
          // Ensure user logged in with correct policy (not resetpassword or other)
          if (
            response.account &&
            !response.account.homeAccountId.includes(policies.signin.toLowerCase())
          ) {
            await instance.logoutRedirect();
            dispatch(signOut());
            instance.loginRedirect({
              authority: import.meta.env.VITE_B2C_CONFIG_INSTANCE,
              scopes: loginScopes,
            });
          }
        }
      } catch (error: unknown) {
        logger.error("B2C policy redirect handling failed", error);

        // Handle AADB2C90118: User forgot password
        // User clicked "Forgot password?" link on B2C signin page
        const b2cError = error as { errorMessage?: string };
        if (b2cError.errorMessage && b2cError.errorMessage.indexOf("AADB2C90118") > -1) {
          instance.loginRedirect({
            authority: import.meta.env.VITE_B2C_CONFIG_INSTANCE.replace(
              policies.signin,
              policies.resetpassword,
            ),
            scopes: loginScopes,
          });
        }

        // Handle AADB2C90091: User cancelled flow
        // User clicked "Cancel" on B2C page (signup/reset password)
        if (b2cError.errorMessage && b2cError.errorMessage.indexOf("AADB2C90091") > -1) {
          await instance.logoutRedirect();
          dispatch(signOut());
          instance.loginRedirect({
            authority: import.meta.env.VITE_B2C_CONFIG_INSTANCE,
            scopes: loginScopes,
          });
        }
      }
    };

    handleRedirectPromise();
  }, [instance, dispatch]);

  /**
   * Check if authentication interaction is in progress
   */
  const loginInProgress = useCallback((): boolean => {
    return (
      sessionStorage.getItem(`msal.${b2cConfig.clientId}.interaction.status`) ===
      "interaction_in_progress"
    );
  }, []);

  /**
   * Initiate password reset flow
   * Redirects to B2C password reset policy
   */
  const resetPassword = useCallback(async (): Promise<void> => {
    if (loginInProgress()) {
      logger.warn("Authentication interaction already in progress");
      return;
    }

    try {
      const resetPasswordAuthority = b2cConfig.instance.replace(
        b2cConfig.policies.signin,
        b2cConfig.policies.resetpassword,
      );

      await instance.loginRedirect({
        authority: resetPasswordAuthority,
        scopes: loginScopes,
      });
    } catch (error) {
      logger.error("Password reset failed", error);
      throw error;
    }
  }, [instance, loginInProgress]);

  /**
   * Handle B2C error codes
   * Returns true if error was handled, false otherwise
   *
   * @param error Error object from MSAL
   * @returns Promise<boolean> indicating if error was handled
   */
  const handleB2CError = useCallback(
    async (error: unknown): Promise<boolean> => {
      const b2cErr = error as { errorMessage?: string; message?: string };
      const errorMessage = b2cErr?.errorMessage || b2cErr?.message || "";

      // AADB2C90091: User cancelled the flow
      if (errorMessage.indexOf("AADB2C90091") > -1) {
        if (!loginInProgress()) {
          // Restart login flow
          await instance.loginRedirect({ scopes: loginScopes });
          return true;
        }
      }

      // AADB2C90118: User forgot password
      if (errorMessage.indexOf("AADB2C90118") > -1) {
        await resetPassword();
        return true;
      }

      return false;
    },
    [instance, loginInProgress, resetPassword],
  );

  /**
   * Validate if account belongs to correct policy
   * @param homeAccountId Account's home account ID
   * @returns boolean indicating if account is valid for signin policy
   */
  const isValidSignInAccount = useCallback((homeAccountId: string): boolean => {
    return homeAccountId.toLowerCase().includes(b2cConfig.policies.signin.toLowerCase());
  }, []);

  return {
    // Policy-based login methods
    resetPassword,

    // Error handling
    handleB2CError,

    // Validation
    isValidSignInAccount,

    // State
    loginInProgress: loginInProgress(),

    // Config access (for advanced use cases)
    policies: b2cConfig.policies,
  };
}
